import React, { useEffect, useState } from 'react';
import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { createNewDiet } from '../../../redux/actions/dietActions';
import { fetchAllFoodsAndTaco4Foods } from '../../../redux/actions/foodActions';
import { fetchAllUnitMeasurements } from '../../../redux/actions/unit_measurementsActions';
import { fetchCompanies, fetchPatients, fetchAllProfessionals } from '../../../redux/actions/relatedEntitiesActions';

import { Formik, Field, Form, FieldArray, ErrorMessage } from 'formik';
import * as Yup from 'yup';

import { FaPlusCircle, FaMinusCircle } from "react-icons/fa";

import Select from 'react-select';
import { Tabs, Tab } from '../../../components/Tabs/TabsDiets';
import Tips from '../../../components/Tips/Tips';
import {
  formatDietGroup, formatPersonType, formatSpeciality, onlyNumbers,
  availableDiets, availableMeals, availableJustifications, availableDietTypes,
  calculateMealCalories, selectOptions,
} from '../../../utils/functionsFormatPatterns/functionFormatDiet';
import '../styles/CreateDiet.css';
import '../../../styles/ReactSelect.css';
import { useModalFeedback } from '../../../context/ModalContext';

const CreateDiet = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { showInfo } = useModalFeedback();

  const { foodsAndTaco4Foods = [], food4Loading, food4Error } = useSelector((state) => state.foodsAndTacoFoods);

  const [isFetched, setIsFetched] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const unitMeasurementState = useSelector((state) => state.unit_measurement) || {};
  const { unit_measurements = [], loading: umLoading, error: umError } = useSelector((state) => unitMeasurementState);

  const { companies, patients, professionals, loading, error } = useSelector((state) => state.relatedEntities);

  // console.log('unit_measurements:', unit_measurements);

  const appointment_id = location.state?.appointment_id;
  // console.log(appointment_id)
  const patient_id = location.state?.patient_id;
  // console.log(patient_id)
  const consultation_id = location.state?.consultation_id;
  // console.log(consultation_id)
  const company_id = location.state?.company_id;
  // console.log(company_id)
  const professional_id = location.state?.professional_id;
  // console.log(professional_id)


  useEffect(() => {
    dispatch(fetchCompanies());
    dispatch(fetchPatients());
    dispatch(fetchAllProfessionals());
  }, [dispatch]);


  useEffect(() => {
    if (!isFetched && !loading) {
      dispatch(fetchAllFoodsAndTaco4Foods())
        .unwrap()
        .then((data) => {
          setIsFetched(true);
        })
        .catch((error) => {
          console.error('Erro ao buscar alimentos:', error);
          setIsFetched(true);
        });
    }
  }, [dispatch, loading, isFetched]);


  const initialValues = {
    appointment_id: appointment_id ? Number.parseInt(appointment_id) : '', // Pode ser vazio
    consultation_id: consultation_id || '', // Pode ser vazio
    patient_id: patient_id || '',
    company_id: company_id || '',
    professional_id: professional_id || '',
    diet: '',
    objective: '',
    justification: '', // 🚀 Novo campo para justificar dietas sem consulta
    diet_type: '',
    meals: [
      {
        name: '',
        time: '',
        total_calories: 0,
        foods: [
          {
            food_id: '',
            taco_food_id: '',
            food_name: '',
            quantity: '',
            unit_measurement: '',
            equivalent_value: '',
            kcal: '',
          }
        ],
        selectFood: null,
      },
    ],
  };

  const combinedFoods = useMemo(() => {
    return foodsAndTaco4Foods.map((food) => ({
      value: food.food_id || food.taco4_id || food.taco_food_id,
      label: `${food.food_name} (${food.taco4_id ? 'TACO4' : 'Customizado'})`,
      kcal: food.kcal,
      source: food.taco4_id ? 'taco4_food' : 'foods',
    }));
  }, [foodsAndTaco4Foods]);


  useEffect(() => {
    if (unit_measurements.length === 0) {
      dispatch(fetchAllUnitMeasurements());
    }
  }, [dispatch, unit_measurements.length]);

  if (umLoading) return <div>Carregando Unidades de Medidas...</div>;
  if (umError) return <div>Erro ao carregar unidades de medidas: {umError}</div>;



  const validationSchema = Yup.object({
    patient_id: Yup.string().required('O paciente é obrigatório.'),
    company_id: Yup.string()
      .when("appointment_id", {
        is: (appointment_id) => appointment_id, // Se houver consulta
        then: (schema) => schema.required("A empresa é obrigatória para dietas com consulta"),
        otherwise: (schema) => schema.notRequired(),
      }),
    professional_id: Yup.string()
      .when("appointment_id", {
        is: (appointment_id) => appointment_id, // Se houver consulta
        then: (schema) => schema.required("O profissional é obrigatório para dietas com consulta"),
        otherwise: (schema) => schema.notRequired(),
      }),
    diet: Yup.string().required('O nome da dieta é obrigatório'),
    objective: Yup.string().required('O objetivo da dieta é obrigatório'),
    justification: Yup.string()
      .nullable()
      .when("appointment_id", {
        is: (appointment_id) => !appointment_id, // Se não houver consulta
        then: (schema) => schema.required("Uma justificativa é necessária para criar a dieta sem consulta"),
        otherwise: (schema) => schema.notRequired(),
      }),

    diet_type: Yup.string()
      .nullable()
      .when("appointment_id", {
        is: (appointment_id) => !appointment_id, // Se for dieta sem consulta
        then: (schema) => schema.required("O tipo de dieta é obrigatório"),
        otherwise: (schema) => schema.notRequired(),
      }),
    meals: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('Selecione uma refeição'),
        time: Yup.string().required('O horário da refeição é obrigatório'),
        total_calories: Yup.number().min(1, 'Calorias inválidas').required('O total de calorias é obrigatório'),
        foods: Yup.array().of(
          Yup.object().shape({
            quantity: Yup.number().min(1, 'Quantidade inválida').required('A quantidade é obrigatória'),
            unit_measurement: Yup.string().required('A unidade de medida é obrigatória'),
          })
        ),
      })
    ),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const formattedMeals = values.meals.map((meal) => {
        const foods = meal.foods.map((food) => {
          const kcalPer100g = parseFloat(food.kcal) || 0;
          const quantity = parseFloat(food.quantity) || 0;
          const equivalent = parseFloat(food.equivalent_value) || 1;
          const quantityInGrams = quantity * equivalent;
          const calories = ((quantityInGrams * kcalPer100g) / 100).toFixed(2);

          return {
            food_id: food.food_id || null,
            taco_food_id: food.taco_food_id || null,
            // taco4_id: food.taco4_id || null,
            food_name: food.food_name,
            quantity: food.quantity,
            unit_measurement: food.unit_measurement,
            calories,
            equivalent_value: food.equivalent_value || 1,
          };
        });

        return {
          name: meal.name,
          time: meal.time,
          total_calories: calculateMealCalories(meal.foods),
          foods,
        };
      });


      const dietTotalCalories = formattedMeals.reduce((acc, meal) => acc + meal.total_calories, 0);

      console.log('Final Diet Calories:', dietTotalCalories);
      console.log('Formatted Meals:', formattedMeals);


      const formData = {
        patient_id: Number(values.patient_id),
        appointment_id: values.appointment_id ? Number(values.appointment_id) : null,
        consultation_id: values.consultation_id ? Number(values.consultation_id) : null,
        company_id: values.company_id ? Number(values.company_id) : null,
        professional_id: values.professional_id ? Number(values.professional_id) : null,
        name: values.diet,
        objective: values.objective,
        justification: values.justification || null, // 🚀 Adicionado
        diet_type: values.diet_type || null,
        diet_total_calories: dietTotalCalories.toFixed(2),
        meals: formattedMeals,
      };

      await dispatch(createNewDiet({ formData })).unwrap();
      console.log("Form da Dieta enviada: " + JSON.stringify(formData));
      // setMessage('Dieta criada com sucesso!');
      // setShowModal(true);

      // setTimeout(() => {
      //   setShowModal(false);
      //   if (values.appointment_id) {
      //     navigate(`/home/consultation/update/${values.appointment_id}`, {
      //       state: {
      //         consultation_id: values.consultation_id,
      //         patient_id: values.patient_id,
      //         appointment_id: values.appointment_id,
      //       },
      //     });
      //   } else {
      //     navigate('/home/diet/get');
      //   }
      // }, 2000);
      showInfo('Dieta criada com sucesso!');
      if (values.appointment_id) {
        navigate(`/home/consultation/update/${values.appointment_id}`, {
          state: {
            consultation_id: values.consultation_id,
            patient_id: values.patient_id,
            appointment_id: values.appointment_id,
          },
        });
      } else {
        navigate('/home/diet/get');
      };

    } catch (error) {
      // console.error('Erro ao criar dieta:', error);
      // setMessage('Erro ao criar dieta. Tente novamente.');
      // setShowModal(true);
      // setTimeout(() => setShowModal(false), 2500);
      showInfo('Erro ao criar dieta. Tente novamente.', 3000);
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="dietMain">
      <h3>Nova Dieta</h3>
      <div className="dietForm">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, isSubmitting, touched, errors, isValid, setFieldValue }) => (
            <Form>
              {/* {appointment_id || consultation_id ? (
                <div>Relacionado ao Agendamento
                  <div>Agendamento id: {appointment_id}</div>
                  <div>Paciente id: {patient_id}</div>
                  <div>Consulta id: {consultation_id}</div>
                </div>
              ) : (
                <p>ID do agendamento ou da consulta não fornecido.</p>
              )} */}
              <FieldArray name="meals">
                {({ push, remove }) => (
                  <>
                    {(!patient_id || !appointment_id) &&
                      <div className="dietLine">
                        <label htmlFor="patient_id">Paciente:</label>
                        <Select
                          name="patient_id"
                          options={selectOptions(patients)}
                          noOptionsMessage={() => "Nenhuma opção disponível"}
                          onChange={option => setFieldValue('patient_id', option ? option.value : '')}
                          classNamePrefix="custom-select"
                          placeholder="Selecione o paciente"
                        />
                        <ErrorMessage name="patient_id" component="div" className="error-message" />

                        <label htmlFor="company_id">Empresa:</label>
                        <Select
                          name="company_id"
                          options={selectOptions(companies, 'company_name')}
                          noOptionsMessage={() => "Nenhuma opção disponível"}
                          onChange={option => setFieldValue('company_id', option ? option.value : '')}
                          classNamePrefix="custom-select"
                          placeholder="Selecione a empresa"
                        />
                        <ErrorMessage name="company_id" component="div" className="error-message" />

                        <label htmlFor="professional_id">Profissional:</label>
                        <Select
                          name="professional_id"
                          options={selectOptions(professionals, 'name')}
                          noOptionsMessage={() => "Nenhuma opção disponível"}
                          onChange={option => setFieldValue('professional_id', option ? option.value : '')}
                          classNamePrefix="custom-select"
                          placeholder="Selecione o profissional"
                          formatOptionLabel={option => (
                            <>
                              {option.label}
                              {option.person_type && ` - ${formatPersonType(option.person_type)}`}
                              {option.speciality && ` - ${formatSpeciality(option.speciality)}`}
                            </>
                          )}
                        />
                        <ErrorMessage name="professional_id" component="div" className="error-message" />

                        <label>Justificativa:</label>
                        <Select
                          name="justification"
                          options={availableJustifications}
                          noOptionsMessage={() => "Nenhuma opção disponível"}
                          onChange={(option) => setFieldValue("justification", option ? option.value : "")}
                          classNamePrefix="custom-select"
                          placeholder="Selecione uma justificativa"
                          formatOptionLabel={(option) => <>{option.label}</>}
                        />
                        <ErrorMessage name="justification" component="div" className="error-message" />

                      </div>
                    }
                    <div className="dietLine">
                      <label>Tipo de Dieta:</label>
                      <Select
                        name="diet_type"
                        options={availableDietTypes}
                        noOptionsMessage={() => "Nenhuma opção disponível"}
                        onChange={(option) => setFieldValue("diet_type", option ? option.value : "")}
                        classNamePrefix="custom-select"
                        placeholder="Selecione um tipo de dieta"
                        formatOptionLabel={(option) => <>{option.label}</>}
                      />
                      <ErrorMessage name="diet_type" component="div" className="error-message" />
                    </div>

                    <div className="dietLine createDiet">

                      <div className="diet-form-group">
                        <label>Dieta:</label>
                        <Field
                          as="select"
                          name="diet"
                          onChange={(e) => {
                            const selectedValue = e.target.value;
                            const selectedDiet = Object.values(availableDiets)
                              .flat()
                              .find((diet) => diet.value === selectedValue);

                            // Atualiza os campos "diet" e "objective"
                            setFieldValue('diet', selectedValue);
                            setFieldValue('objective', selectedDiet?.objective || '');
                          }}
                        >
                          <option value="">Selecione</option>
                          {Object.entries(availableDiets).map(([category, diets]) => (
                            <optgroup key={category} label={formatDietGroup(category)}>
                              {diets.map((dietOption) => (
                                <option key={dietOption.value} value={dietOption.value}>
                                  {dietOption.label}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </Field>
                        <ErrorMessage name="diet" component="div" className="error-message" />
                      </div>

                      <div className="diet-form-group">
                        <label>Objetivo:</label>
                        <Field
                          className="objectiveField"
                          id="objectiveField"
                          name="objective"
                          type="text"
                          readOnly
                        />
                        <ErrorMessage name="objective" component="div" className="error-message" />
                      </div>

                      <div className="diet-form-group">
                        <label>Total de Calorias desta Dieta:</label>
                        <Field
                          className="diet_total-calories"
                          name="diet_total_calories"
                          type="text"
                          value={values.meals.reduce((acc, meal) => acc + calculateMealCalories(meal.foods), 0).toFixed(2)}
                          readOnly
                          disabled
                        />

                        <ErrorMessage name="diet_total_calories" component="div" className="error-message" />
                      </div>

                    </div>
                    <div className="dietLine">
                      {/* Botão para adicionar uma nova refeição */}
                      <button
                        className='addMeal'
                        type="button"
                        onClick={() => {
                          push({ name: '', time: '', total_calories: '', foods: [] });
                          setActiveTab(values.meals.length);
                        }}
                      >
                        <FaPlusCircle /> Adicionar Refeição
                      </button>
                    </div>
                    <div className='dietTabs'>
                      <Tabs activeTab={activeTab} onChange={setActiveTab}>
                        {values.meals.map((meal, mealIndex) => (
                          <Tab key={mealIndex} label={meal.name || `Refeição ${mealIndex + 1}`}>
                            <div className="meal-group">
                              <div className="dietLine">
                                <div className="diet-form-group">
                                  <label>Refeição:</label>
                                  <Field
                                    as="select"
                                    name={`meals[${mealIndex}].name`}
                                    onChange={(e) => {
                                      // Atualiza o nome da refeição sem alterar a aba ativa
                                      setFieldValue(`meals[${mealIndex}].name`, e.target.value);
                                    }}
                                  >
                                    <option value="">Selecione</option>
                                    {availableMeals.map((mealOption) => (
                                      <option key={mealOption.value} value={mealOption.label}>
                                        {mealOption.label}
                                      </option>
                                    ))}
                                  </Field>
                                  <ErrorMessage name={`meals[${mealIndex}].name`} component="div" className="error-message" />
                                </div>

                                <div className="diet-form-group" id="time-meal">
                                  <label>Horário:</label>
                                  <Field
                                    id="time-meal"
                                    name={`meals[${mealIndex}].time`}
                                    type="time"
                                  />
                                  <ErrorMessage name={`meals[${mealIndex}].time`} component="div" className="error-message" />
                                </div>

                                <div className="diet-form-group">
                                  <label>Total de Calorias desta Refeição:</label>
                                  <Field
                                    name={`meals[${mealIndex}].total_calories`}
                                    type="text"
                                    readOnly
                                    value={calculateMealCalories(values.meals[mealIndex].foods).toFixed(2)}
                                  />


                                  <ErrorMessage name={`meals[${mealIndex}].total_calories`} component="div" className="error-message" />
                                </div>
                                <Tips id="tip-totalCalories" message="Determine o valor total desta refeição independentemente do valor por cada 100gr apresentado em cada alimento." />
                              </div>

                              <FieldArray name={`meals[${mealIndex}].foods`}>
                                {({ push: pushFood, remove: removeFood, replace }) => (
                                  <>
                                    <div className="diet-form-group">
                                      <label htmlFor="foods">Selecione um Alimento</label>
                                      <Select
                                        options={combinedFoods}
                                        noOptionsMessage={() => "Nenhum alimento disponível"}
                                        value={values.meals[mealIndex].selectFood || null}
                                        onChange={(selectedOption) => {
                                          if (selectedOption) {
                                            const newFood = {
                                              food_id: selectedOption.source === 'foods' ? selectedOption.value : null,
                                              taco_food_id: selectedOption.source === 'taco4_food' ? selectedOption.value : null,
                                              food_name: selectedOption.label,
                                              quantity: '',
                                              unit_measurement: '',
                                              kcal: selectedOption.kcal || 0,
                                            };

                                            const emptyIndex = values.meals[mealIndex].foods.findIndex(
                                              (food) => !food.food_id && !food.taco_food_id
                                            );

                                            if (emptyIndex !== -1) {
                                              replace(emptyIndex, newFood);
                                            } else {
                                              pushFood(newFood);
                                            }

                                            setFieldValue(`meals[${mealIndex}].selectFood`, null);
                                          }
                                        }}
                                        isClearable
                                        placeholder="Buscar alimento..."
                                      />
                                    </div>

                                    <ul>
                                      {values.meals[mealIndex].foods.map((food, foodIndex) => (
                                        <li key={foodIndex}>
                                          <div className="dietLine">
                                            <div className="diet-form-group">
                                              <label>Quantidade:</label>
                                              <Field
                                                className="foodListField"
                                                name={`meals[${mealIndex}].foods[${foodIndex}].quantity`}
                                                type="number"
                                                onKeyDown={(e) => onlyNumbers(e, setFieldValue, `meals[${mealIndex}].foods[${foodIndex}].quantity`)}
                                                onChange={(e) => {
                                                  setFieldValue(`meals[${mealIndex}].foods[${foodIndex}].quantity`, e.target.value);

                                                  // Recalcular calorias totais da refeição
                                                  const updatedFoods = [...values.meals[mealIndex].foods];
                                                  updatedFoods[foodIndex].quantity = e.target.value;
                                                  const totalCalories = calculateMealCalories(updatedFoods);
                                                  setFieldValue(`meals[${mealIndex}].total_calories`, totalCalories.toFixed(2));
                                                }}
                                              />

                                              <ErrorMessage
                                                name={`meals[${mealIndex}].foods[${foodIndex}].quantity`}
                                                component="div"
                                                className="error-message"
                                              />
                                            </div>

                                            <div className="diet-form-group">
                                              <label>Unid. Medida:</label>
                                              <Field
                                                className="foodListField"
                                                as="select"
                                                name={`meals[${mealIndex}].foods[${foodIndex}].unit_measurement`}
                                                onChange={(e) => {
                                                  const selectedUnit = unit_measurements.find(unit => unit.unit_name === e.target.value);
                                                  setFieldValue(`meals[${mealIndex}].foods[${foodIndex}].unit_measurement`, e.target.value || '');
                                                  // setFieldValue(`meals[${mealIndex}].foods[${foodIndex}].equivalent_value`, selectedUnit ? `${selectedUnit.equivalent_value} ${selectedUnit.unit_abbreviation}` : '');
                                                  setFieldValue(`meals[${mealIndex}].foods[${foodIndex}].equivalent_value`, selectedUnit ? selectedUnit.equivalent_value : '');
                                                }}
                                              >
                                                <option value="">Selecione</option>
                                                {unit_measurements.map((units) => (
                                                  <option key={units.unit_measurement_id} value={units.unit_name}>
                                                    {units.unit_name}
                                                  </option>
                                                ))}
                                              </Field>

                                              <ErrorMessage
                                                name={`meals[${mealIndex}].foods[${foodIndex}].unit_measurement`}
                                                component="div"
                                                className="error-message"
                                              />
                                            </div>

                                            <div className="diet-form-group">
                                              <label>Equivalência:</label>
                                              <Field
                                                className="foodListField"
                                                name={`meals[${mealIndex}].foods[${foodIndex}].equivalent_value`}
                                                type="text"
                                                readOnly
                                              />
                                              <ErrorMessage
                                                name={`meals[${mealIndex}].foods[${foodIndex}].equivalent_value`}
                                                component="div"
                                                className="error-message"
                                              />
                                            </div>

                                            <div className="diet-form-group foodName">
                                              <label>Alimento:</label>
                                              <strong>{`${food.food_name || "Novo Alimento"} - ${food.kcal || 0} kcal`}</strong>
                                            </div>
                                            <button
                                              className="btnIcoAddRm"
                                              type="button"
                                              onClick={() => removeFood(foodIndex)}
                                            >
                                              <FaMinusCircle /> Remover Alimento
                                            </button>
                                          </div>
                                        </li>
                                      ))}
                                    </ul>

                                    <button
                                      className="btnIcoAddRm"
                                      type="button"
                                      onClick={() => {
                                        if (
                                          values.meals[mealIndex].foods.length === 0 ||
                                          values.meals[mealIndex].foods.every((food) => food.food_id || food.taco_food_id)
                                        ) {
                                          pushFood({ food_id: '', quantity: '', unit_measurement: '', kcal: '' });
                                        }
                                      }}
                                    >
                                      <FaPlusCircle /> Adicionar Alimento
                                    </button>
                                  </>
                                )}
                              </FieldArray>

                              <div className='mealButtonLine'>
                                <button
                                  className='removeMeal'
                                  type="button"
                                  onClick={() => {
                                    if (values.meals.length > 1) {
                                      remove(mealIndex);
                                      setActiveTab(mealIndex > 0 ? mealIndex - 1 : 0);
                                    }
                                  }}
                                >
                                  Remover Refeição
                                </button>
                              </div>
                            </div>
                          </Tab>
                        ))}
                      </Tabs>
                    </div>
                  </>
                )}
              </FieldArray>

              <div className="dietButtons">
                <button
                  className='sendBtn'
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Criando...' : 'Criar Dieta'}
                </button>

                <button
                  className='backBtn'
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => {
                    if (consultation_id) {
                      navigate(`/home/consultation/show/${consultation_id}`);
                    } else {
                      navigate('/home/diet/get/');
                    }
                  }}
                >
                  {isSubmitting ? 'Saindo...' : 'Cancelar'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CreateDiet;